import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, ExternalLink, Calendar, User, Users, Edit, Trash2 } from 'lucide-react';

interface Demo {
  id: string;
  title: string;
  description: string;
  demo_url: string;
  thumbnail_url: string;
  created_by_email: string;
  created_at: string;
  updated_at: string;
  tags: string[];
  status: string;
  shares?: any[];
  shared_by_email?: string;
  shared_at?: string;
  permission?: string;
}

const IndexSectionDashboardNavigations2: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [myDemos, setMyDemos] = useState<Demo[]>([]);
  const [sharedDemos, setSharedDemos] = useState<Demo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'my' | 'shared'>('my');

  useEffect(() => {
    if (user?.email) {
      fetchDemos();
    }
  }, [user]);

  const fetchDemos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/demos?userEmail=${encodeURIComponent(user?.email || '')}`);
      const data = await response.json();
      
      if (data.success) {
        setMyDemos(data.myDemos || []);
        setSharedDemos(data.sharedDemos || []);
      } else {
        setError(data.error || 'Failed to load demos');
      }
    } catch (err) {
      setError('Failed to load demos');
      console.error('Error fetching demos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (demoId: string) => {
    if (!confirm('Are you sure you want to delete this demo?')) return;

    try {
      const response = await fetch(`/api/demos/${demoId}?userEmail=${encodeURIComponent(user?.email || '')}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      
      if (data.success) {
        fetchDemos();
      } else {
        alert(data.error || 'Failed to delete demo');
      }
    } catch (err) {
      alert('Failed to delete demo');
      console.error('Error deleting demo:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const DemoCard = ({ demo, isShared = false }: { demo: Demo; isShared?: boolean }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{demo.title}</h3>
          {demo.description && (
            <p className="text-sm text-gray-600 mb-2 line-clamp-2">{demo.description}</p>
          )}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(demo.updated_at)}</span>
            </div>
            {isShared ? (
              <div className="flex items-center gap-1">
                <User className="w-3 h-3" />
                <span>Shared by: {demo.shared_by_email}</span>
              </div>
            ) : (
              demo.shares && demo.shares.length > 0 && (
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  <span>Shared with {demo.shares.length} {demo.shares.length === 1 ? 'person' : 'people'}</span>
                </div>
              )
            )}
          </div>
          {demo.tags && demo.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {demo.tags.map((tag, index) => (
                <span key={index} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2 ml-4">
          {demo.demo_url && (
            <a
              href={demo.demo_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-purple-600 hover:bg-purple-50 rounded transition-colors"
              title="Open demo"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          {!isShared && (
            <>
              <button
                onClick={() => navigate(`/demos/${demo.id}`)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="Edit demo"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(demo.id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Delete demo"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
          {isShared && demo.permission === 'edit' && (
            <button
              onClick={() => navigate(`/demos/${demo.id}`)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
              title="Edit demo"
            >
              <Edit className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">My Demos</h1>
          <p className="text-gray-600">Create, manage, and share your demo library</p>
        </div>
        <button
          onClick={() => navigate('/demos/new')}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create New Demo
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('my')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'my'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            My Demos ({myDemos.length})
          </button>
          <button
            onClick={() => setActiveTab('shared')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'shared'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Shared with Me ({sharedDemos.length})
          </button>
        </nav>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading demos...</div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      ) : (
        <div className="space-y-4">
          {activeTab === 'my' ? (
            myDemos.length > 0 ? (
              myDemos.map((demo) => <DemoCard key={demo.id} demo={demo} />)
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">You haven't created any demos yet</p>
                <button
                  onClick={() => navigate('/demos/new')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Create Your First Demo
                </button>
              </div>
            )
          ) : (
            sharedDemos.length > 0 ? (
              sharedDemos.map((demo) => <DemoCard key={demo.id} demo={demo} isShared />)
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No demos have been shared with you yet</p>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default IndexSectionDashboardNavigations2;
